import React, { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { PageHeader, Section, EmptyState, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { toast, Toaster } from "sonner";
import { Sparkles, Check, X, Clock } from "lucide-react";

const STATUT = {
  en_attente: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approuve: { label: "Approuvée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  refuse: { label: "Refusée", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function AiQuotaRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extra, setExtra] = useState(20);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/assistant/quota-requests").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, approve) => {
    setBusy(id);
    try {
      await api.post(`/assistant/quota-requests/${id}/decide`, { approve, extra: Number(extra) || 20 });
      toast.success(approve ? `Déblocage accordé (+${extra} messages).` : "Demande refusée.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy("");
    }
  };

  const pending = items.filter((x) => x.statut === "en_attente");
  const decided = items.filter((x) => x.statut !== "en_attente");

  return (
    <div className="p-8" data-testid="ai-quota-requests-page">
      <Toaster position="top-right" richColors />
      <PageHeader title="Demandes d'accès IA" subtitle="Débloquez des messages supplémentaires pour vos vendeurs" />

      <div className="max-w-3xl space-y-6">
        <Section title={`En attente (${pending.length})`} actions={<Clock className="w-4 h-4 text-amber-500" />}>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-4 text-sm">
              <label className="text-slate-500">Messages à accorder par déblocage :</label>
              <input type="number" min="1" value={extra} onChange={(e) => setExtra(e.target.value)}
                data-testid="extra-grant-input"
                className="w-20 px-2 py-1.5 border border-slate-300 rounded-sm text-sm text-right focus:outline-none focus:ring-1 focus:ring-[#B8873A]" />
            </div>
            {loading ? (
              <div className="text-slate-400 text-sm py-4">Chargement…</div>
            ) : pending.length === 0 ? (
              <EmptyState icon={Sparkles} title="Aucune demande en attente" description="Les demandes de déblocage apparaîtront ici." testid="no-pending-requests" />
            ) : (
              <div className="space-y-3">
                {pending.map((r) => (
                  <div key={r.id} className="flex items-center justify-between border border-slate-200 rounded-sm px-4 py-3" data-testid={`request-${r.id}`}>
                    <div>
                      <div className="font-medium text-[#111111]">{r.user_name} <span className="text-xs text-slate-400">· {ROLE_LABELS[r.user_role] || r.user_role}</span></div>
                      <div className="text-xs text-slate-500">A utilisé {r.used_at_request} messages · {new Date(r.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <div className="flex gap-2">
                      <PrimaryButton onClick={() => decide(r.id, true)} disabled={busy === r.id} data-testid={`approve-${r.id}`}>
                        <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Débloquer</span>
                      </PrimaryButton>
                      <SecondaryButton onClick={() => decide(r.id, false)} disabled={busy === r.id} data-testid={`reject-${r.id}`}>
                        <span className="flex items-center gap-1"><X className="w-4 h-4" /> Refuser</span>
                      </SecondaryButton>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {decided.length > 0 && (
          <Section title="Décisions récentes (aujourd'hui)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-3">Vendeur</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3 text-right">Messages accordés</th>
                    <th className="px-4 py-3">Décidé par</th>
                  </tr>
                </thead>
                <tbody>
                  {decided.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium">{r.user_name}</td>
                      <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${STATUT[r.statut]?.cls}`}>{STATUT[r.statut]?.label}</span></td>
                      <td className="px-4 py-3 text-right">{r.extra_granted || 0}</td>
                      <td className="px-4 py-3 text-slate-500">{r.decided_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
